<template>
  <div class="message-thread">
    <section class="message-thread__preview">
      <div class="message-thread__message">
        <MessageAuthorAvatar
          :avatar-url="authorAvatar"
          :message-id="messageId"
          @open:profile="handleProfileOpen"
        />
        <div style="display: flex; flex-direction: column; gap: 0; width: 100%; position: relative">
          <MessageHeader
            :message-id="messageId"
            @open:profile="handleProfileOpen"
          />
          <div class="message-thread__message-body">
            <iMessageSegmentsPreview
              v-if="message.segments.length"
              :segments="message.segments"
              :preview-link-data="previewLinkData"
              :author-name="authorName"
              :author-avatar="authorAvatar"
              :created-at="createdAt"
              variant="slack"
              @get:link-preview="handleGetLinkPreviewData"
            />
            <iText v-else class="message-thread__text" data-test="message-thread-text">
              {{ message.message }}
            </iText>
          </div>
          <ModerationInfo :message-id="messageId" />
        </div>
      </div>

      <div class="message-thread__reply-count">
        <span v-if="threadReplies.length" class="message-thread__reply-count-text">
          {{ threadReplies.length }} {{ threadReplies.length === 1 ? "Reply" : "Replies" }}
        </span>
        <div class="message-thread__divider" />
      </div>
    </section>

    <section class="message-thread__replies">
      <div v-if="!threadReplies.length" class="message-thread__empty">
        <span>No replies yet.</span>
      </div>
      <div
        v-for="reply in threadReplies"
        v-else
        :id="'message-' + reply.messageId"
        :key="reply.messageId"
        class="message-thread__reply"
      >
        <div
          class="message-thread__reply-container"
          :data-force-hover-state="reactionPickerVisible"
        >
          <div class="message-thread__message message-thread__message--reply">
            <MessageAuthorAvatar
              :avatar-url="getReplyAuthorAvatar(reply)"
              :message-id="reply.messageId"
              @open:profile="handleProfileOpen"
            />
            <div style="display: flex; flex-direction: column; gap: 0; width: 100%; position: relative">
              <MessageHeader
                :message-id="reply.messageId"
                @open:profile="handleProfileOpen"
              />
              <Message
                :message-id="reply.messageId"
                :context="context"
                :entity-id="entityId"
                @toggle:reaction-picker="reactionPickerVisible = $event"
              />
              <MessageFooter
                :message-id="reply.messageId"
                @toggle:reaction-picker="reactionPickerVisible = $event"
              />
            </div>
          </div>
        </div>
      </div>
    </section>

    <footer class="message-thread__editor">
      <CreateMessage
        :entity-id="messageId"
        context="reply"
        :parent-context="context"
        :parent-entity-id="entityId"
      />
    </footer>
  </div>
</template>

<script>
import { mapActions, mapState } from "pinia";
import dayjs from "dayjs";
import { useMessageStore } from "%/apps/conversations/stores/message-store";
import { useGeneralStore } from "%/stores/general-store";
import ModerationInfo from "%/apps/conversations/components/message/ModerationInfo.vue";
import MessageAuthorAvatar from "%/apps/conversations/components/message/MessageAuthorAvatar.vue";
import MessageHeader from "%/apps/conversations/components/message/MessageHeader.vue";
import CreateMessage from "%/apps/conversations/components/message/CreateMessage.vue";
import Message from "%/apps/conversations/components/message/Message.vue";
import MessageFooter from "%/apps/conversations/components/message/MessageFooter.vue";
import { DEFAULT_USER_AVATAR } from "%/constants/user-constants";

export default {
  name: "MessageThread",
  components: {
    MessageFooter,
    Message,
    CreateMessage,
    MessageHeader,
    MessageAuthorAvatar,
    ModerationInfo,
  },
  props: {
    messageId: {
      type: Number,
      required: true,
    },
    context: {
      type: String,
      required: true,
    },
    entityId: {
      type: Number,
      required: true,
    },
  },
  emits: ["open:profile"],
  data() {
    return {
      previewLinkData: null,
      reactionPickerVisible: false,
    };
  },
  computed: {
    ...mapState(useGeneralStore, ["userData"]),
    message() {
      const message = this.getMessage(this.messageId);

      if (!message) {
        return {
          messageId: this.messageId,
          message: "",
          segments: [],
          replies: [],
          timestampCreated: 0,
          authorName: "",
          avatarUrl: null,
        };
      }

      return message;
    },
    createdAt() {
      const timestamp = Number(this.message.timestampCreated);

      if (!timestamp) {
        return "";
      }

      return dayjs.unix(timestamp).format("hh:mm A");
    },
    isAuthor() {
      return this.isMessageAuthor(this.messageId);
    },
    authorName() {
      if (this.isAuthor) {
        return this.userData.first_name;
      }
      return this.message.authorName;
    },
    authorAvatar() {
      if (this.isAuthor) {
        return this.userData.avatar_url;
      }
      return this.message.avatarUrl || DEFAULT_USER_AVATAR;
    },
    threadReplies() {
      return this.message.replies || [];
    },
  },
  created() {
    if (this.threadReplies) {
      this.updateMessagesFromResponse(this.message.replies, null);
    }
  },
  methods: {
    ...mapActions(useMessageStore, [
      "getMessage",
      "getLinkPreviewData",
      "isMessageAuthor",
      "updateMessagesFromResponse",
    ]),
    getReplyAuthorAvatar(reply) {
      if (this.isMessageAuthor(reply.messageId)) {
        return this.userData.avatar_url;
      }
      return reply.avatarUrl ?? DEFAULT_USER_AVATAR;
    },
    handleGetLinkPreviewData(url) {
      if (!url) {
        this.previewLinkData = null;
        return;
      }

      if (dayjs.unix(this.message.timestampCreated).isBefore(dayjs().subtract(5, "minute"))) {
        this.previewLinkData = null;
        return;
      }

      this.getLinkPreviewData(url, data => {
        this.previewLinkData = data;
      });
    },
    handleProfileOpen(userId) {
      this.$emit("open:profile", userId);
    },
  },
  styleGuide: () => ({
    containerPadding: { "size.spacing": "extraSmall" },
  }),
};
</script>

<style scoped lang="scss">
.message-thread {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.message-thread__preview {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.message-thread__message {
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-start;
  gap: 6px;
}

.message-thread__message-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.message-thread__message-body {
  display: flex;
  width: 100%;
}

.message-thread__text {
  color: #111827;
  font-size: 14px;
  line-height: 20px;
}

.message-thread__reply-count {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 12px;
}

.message-thread__reply-count-text {
  font-size: 14px;
  color: #6b7280;
  white-space: nowrap;
}

.message-thread__divider {
  flex: 1;
  height: 1px;
  background-color: #e5e7eb;
}

.message-thread__replies {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.message-thread__empty {
  display: flex;
  justify-content: center;
  padding: 16px;
  color: #6b7280;
}

.message-thread__reply {
  display: flex;
}

.message-thread__reply-container {
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
}

.message-thread__message--reply {
  width: 100%;
}

.message-thread__editor {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.message-thread__message,
.message-thread__reply-container {
  padding-left: v-bind("$getStyles.containerPadding");
  padding-right: v-bind("$getStyles.containerPadding");
}
</style>
